// packages/frontend/src/features/recipes/MetadataPills.tsx
//
// Horizontal row of structured-metadata pills below the recipe title.
// Renders nothing if every field is null. Rating renders via the shared
// numeric-pill Rating component (replaces the old star glyphs).
import type { ReactNode } from "react";
import Rating, { type RatingValue } from "./Rating.js";

interface MetadataPillsProps {
  servings: number | null;
  totalTimeMin: number | null;
  activeTimeMin: number | null;
  difficulty: "easy" | "medium" | "hard" | null;
  rating: RatingValue;
}

function formatMinutes(min: number): string {
  if (min < 60) return `${min}m`;
  const h = Math.floor(min / 60);
  const m = min % 60;
  if (m === 0) return `${h}h`;
  return `${h}h ${m}m`;
}

function capitalize(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

export default function MetadataPills({
  servings,
  totalTimeMin,
  activeTimeMin,
  difficulty,
  rating,
}: MetadataPillsProps) {
  const pills: ReactNode[] = [];

  if (servings !== null) {
    pills.push(
      <span key="servings">{`${servings} ${servings === 1 ? "serving" : "servings"}`}</span>,
    );
  }
  if (totalTimeMin !== null) {
    pills.push(<span key="total">{`${formatMinutes(totalTimeMin)} total`}</span>);
  }
  if (activeTimeMin !== null) {
    pills.push(<span key="active">{`${formatMinutes(activeTimeMin)} active`}</span>);
  }
  if (difficulty !== null) {
    pills.push(<span key="difficulty">{capitalize(difficulty)}</span>);
  }
  if (rating !== null) {
    pills.push(<Rating key="rating" value={rating} />);
  }

  if (pills.length === 0) return null;

  return (
    <div className="flex flex-wrap items-center gap-2 text-sm text-zinc-400" data-testid="metadata-pills">
      {pills.flatMap((p, i) => (i === 0 ? [p] : [<span key={`sep-${i}`} aria-hidden="true">·</span>, p]))}
    </div>
  );
}
